import { DateTime } from 'luxon';
import React from 'react';
import { BrowserView, isBrowser, isDesktop } from 'react-device-detect';
import { Link, useNavigate } from 'react-router-dom';

import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Notificacao from 'Interface/Notificacoes';
import { useAppSelector } from 'Store';
import {
  useEditarNotificacaoMutation,
  useRecuperarNotificacaosQuery,
} from 'View/Service/Default/Notificacoes';
import { useRecuperarTarefasQuery } from 'View/Service/Default/Tarefas';

const MenuSuperior = (): React.ReactElement => {
  const navegacao = useNavigate();
  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);
  const [editarNotificacao] = useEditarNotificacaoMutation();

  const { data: { tarefas } = {} } = useRecuperarTarefasQuery(
    {
      contain: ['Proprietarios', 'Atendentes'],
      sort: 'Tarefas.modificado',
      direction: 'desc',
      limit: 10,
    },
    { pollingInterval: 100000 }
  );

  const { data: { notificacoes } = {} } = useRecuperarNotificacaosQuery(
    {
      sort: 'Notificacoes.modificado',
      direction: 'desc',
      limit: 10,
      options: { status: 1 },
    },
    { pollingInterval: 100000 }
  );

  const gerarLink = (notificacao: Notificacao): string => {
    let redirecionamento = '';

    switch (notificacao.tipo) {
      case 'observacao-adicionada-a-tarefa':
        redirecionamento = `/admin/tarefas/editar/${notificacao.dados?.tarefa_id}/observacoes`;
        break;
    }

    return redirecionamento;
  };

  const redirecionar = (notificacao: Notificacao): void => {
    editarNotificacao({ id: notificacao.id, status: 0 }).unwrap();

    navegacao(gerarLink(notificacao));
  };

  const className = (progresso: number): string => {
    let className = 'bg-success';

    if (progresso <= 25) {
      className = 'bg-danger';
    } else if (progresso > 25 && progresso <= 75) {
      className = 'bg-warning';
    }

    return className;
  };

  return (
    <nav
      id="admin-navbar-top"
      className="navbar navbar-expand-lg navbar-dark bg-primary shadow-sm fixed-top"
      style={{ left: isDesktop ? 280 : 0 }}
    >
      <div className="container-fluid mt-2">
        <span className="navbar-brand ms-0 ms-sm-2 ps-1 d-sm-none">
          Estude+
        </span>
        <ul className="navbar-nav ms-auto d-flex flex-row">
          <li className="nav-item">
            <Link to="/estudar" className="nav-link active px-2">
              <Icone nome="escape" />
            </Link>
          </li>
          <li className="nav-item dropdown">
            <a
              className="nav-link active px-2"
              aria-current="page"
              href="#"
              data-bs-toggle="dropdown"
            >
              <span className="position-relative">
                <Icone nome="bell-fill" />
                {notificacoes?.length ? (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-danger p-1">
                    <span className="visually-hidden">Notificações</span>
                  </span>
                ) : null}
              </span>
            </a>
            <ul
              className="dropdown-menu dropdown-menu-end text-small border-0 shadow position-absolute p-0"
              style={{ width: 300 }}
            >
              {notificacoes && notificacoes?.length == 0 ? (
                <li className="text-center py-4 px-3">
                  <Icone nome="bell-slash-fill" className="fs-1 text-primary" />
                  <p className="fs-4 fw-bold mb-0">Nenhuma nova notificação</p>
                  <small className="text-muted">
                    Logo você deve receber alguma!
                  </small>
                </li>
              ) : null}
              {notificacoes?.map((notificacao, indice) => (
                <li
                  key={`${notificacao.id}-${notificacao.modificado}`}
                  className={`d-flex align-items-center gap-2 px-3 py-2 ${indice ? 'border-top' : ''}`}
                >
                  <a
                    className="cursor-pointer"
                    onClick={() => redirecionar(notificacao)}
                  >
                    <Imagem
                      src={notificacao.imagem}
                      width="50"
                      height="50"
                      className="rounded-circle object-fit-cover"
                    />
                  </a>
                  <span className="fw-bold flex-grow-1 text-truncate">
                    <a
                      className="cursor-pointer text-decoration-none text-body"
                      onClick={() => redirecionar(notificacao)}
                    >
                      {notificacao.titulo}
                    </a>
                    <small className="fw-light text-muted d-block">
                      Em{' '}
                      {notificacao.modificado
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </small>
                  </span>
                </li>
              ))}
              {/* <li>
                <Link
                  to="/admin/tarefas"
                  className="btn btn-light btn-sm rounded-0 rounded-bottom d-block"
                >
                  Ver todas as notificações
                </Link>
              </li> */}
            </ul>
          </li>
          <li className="nav-item dropdown">
            <a
              className="nav-link active px-2"
              aria-current="page"
              href="#"
              data-bs-toggle="dropdown"
            >
              <Icone nome="database-fill-check" />
            </a>
            <ul
              className="dropdown-menu dropdown-menu-end text-small border-0 shadow position-absolute p-0"
              style={{ width: 300 }}
            >
              {tarefas && tarefas?.length == 0 ? (
                <li className="text-center py-4 px-3">
                  <Icone nome="database-fill-check" className="fs-1 text-primary" />
                  <p className="fs-5 fw-bold mb-0">Nenhuma atualização recente</p>
                  <small className="text-muted">
                    Tudo tranquilo por aqui!
                  </small>
                </li>
              ) : null}
              {tarefas?.map((tarefa) => (
                <li
                  key={`${tarefa.id}-${tarefa.modificado}`}
                  className="d-flex align-items-center border-bottom gap-2 px-3 py-2"
                >
                  <Link to={`/admin/tarefas/editar/${tarefa.id}`}>
                    <Imagem
                      src={
                        usuario?.id == tarefa.atendente_id
                          ? tarefa.proprietario.imagem
                          : tarefa.atendente.imagem
                      }
                      width="50"
                      height="50"
                      className="rounded-circle object-fit-cover"
                    />
                  </Link>
                  <span className="fw-bold flex-grow-1 text-truncate">
                    <Link
                      to={`/admin/tarefas/editar/${tarefa.id}`}
                      className="text-decoration-none text-body"
                    >
                      {tarefa.titulo}
                    </Link>
                    <small className="fw-light text-muted d-block">
                      Em{' '}
                      {tarefa.modificado
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </small>
                    {tarefa.progresso < 100 && (
                      <div className="progress mt-1" style={{ height: 5 }}>
                        <div
                          className={`progress-bar ${className(
                            tarefa.progresso
                          )}`}
                          style={{ width: `${tarefa.progresso}%` }}
                        ></div>
                      </div>
                    )}
                  </span>
                  {tarefa.status ? (
                    <Icone nome="check-circle" className="text-success" />
                  ) : (
                    <Icone nome="clock" className="text-warning" />
                  )}
                </li>
              ))}
              <li>
                <Link
                  to="/admin/tarefas"
                  className="btn btn-light btn-sm rounded-0 rounded-bottom d-block"
                >
                  Ver todas as tarefas
                </Link>
              </li>
            </ul>
          </li>
          <li className="nav-item dropdown ms-3 d-flex align-items-center">
            <a
              className="nav-link active p-0 d-flex align-items-center"
              aria-current="page"
              href="#"
              data-bs-toggle="dropdown"
            >
              <BrowserView renderWithFragment>
                <span className="d-inline-block">{usuario?.nome}</span>
              </BrowserView>
              <Imagem
                src={usuario?.imagem}
                width="36"
                height="36"
                className={`rounded-circle object-fit-cover ${isBrowser ? 'ms-3' : ''}`}
              />
            </a>
            <ul className="dropdown-menu dropdown-menu-end text-small border-0 shadow position-absolute mr-2">
              <li>
                <Link to="/estudar/alterar-senha" className="dropdown-item">
                  Alterar senha
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={() => navegacao('/login?reset=true')}
                >
                  Sair
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default MenuSuperior;
